<template>
  <Dropdown v-if="canDisplayContext" :key="contextType" @isOpen="(isOpen) => isDropdownOpen = isOpen">
    <template #trigger>
      <button class="btn xs icon transparent">
        <span class="label-text">{{ label }}</span>
        <i class="bi" :class="isDropdownOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
      </button>
    </template>
    <template #menu="{ close }">
      <div v-if="isOrganisationContext && !isOrganisationsPending" class="context-menu">
        <div v-for="organisation in sortedOrganisations" :key="organisation.id" class="context-menu__item" @click="changeOrganisation(organisation); close()"><span class="text-bold">{{ organisation?.details?.name }}</span></div>
      </div>
      <div v-else-if="isSpaceContext && !isSpacesPending" class="context-menu">
        <div v-for="space in sortedSpaces" :key="space.workspace_id" class="context-menu__item" @click="changeSpace(space); close()"><span class="text-bold">{{ space?.name }}</span></div>
      </div>
      <div v-else-if="isCampaignContext && !isCampaignsPending" class="context-menu">
        <div v-for="campaign in sortedCampaigns" :key="campaign.id" class="context-menu__item" @click="changeCampaign(campaign); close()"><span class="text-bold">{{ campaign?.campaign?.internal_name }}</span></div>
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import Dropdown from '@/components/global/Dropdown.vue';
import { useSpace, useSpaces } from '@/queries/space.query';
import { useCampaign, useCampaigns } from '@/queries/campaigns.query';
import { useOrganisation, useOrganisations } from '@/queries/organisation.query';
import { Organisation } from '@/types/company';
import { Space } from '@/types/space';
import { Campaign } from '@/types/campaign';

const router = useRouter();
const appStore = useAppStore();

const props = defineProps<{
  contextType: "organisation" | "space" | "campaign",
}>();

const isDropdownOpen = ref(false);

const { data: currentOrganisation } = useOrganisation();
const { data: currentSpace } = useSpace();
const { data: currentCampaign} = useCampaign();

const { data: organisations, isPending: isOrganisationsPending } = useOrganisations();
const { data: spaces, isPending: isSpacesPending } = useSpaces();
const { data: campaigns, isPending: isCampaignsPending } = useCampaigns();

const sortedOrganisations = computed(() => {
  if (isOrganisationsPending.value) return [];
  return [...organisations.value].sort((a, b) => a.details.name.localeCompare(b.details.name));
});

const sortedSpaces = computed(() => {
  if (isSpacesPending.value) return [];
  return [...spaces.value].sort((a, b) => a.name.localeCompare(b.name));
});

const sortedCampaigns = computed(() => {
  if (isCampaignsPending.value) return [];
  return [...campaigns.value].sort((a, b) => a.campaign.internal_name.localeCompare(b.campaign.internal_name));
});


const isOrganisationContext = computed(() => {
  return props.contextType === "organisation";
});

const isSpaceContext = computed(() => {
  return props.contextType === "space";
});

const isCampaignContext = computed(() => {
  return props.contextType === "campaign";
});

const canDisplayContext = computed(() => {
  return (isOrganisationContext.value && appStore.currentOrganisationId) || (isSpaceContext.value && appStore.currentSpaceId) || (isCampaignContext.value && appStore.currentCampaignId);
});

const label = computed(() => {
  if (isOrganisationContext.value) {
    return `${currentOrganisation?.value?.details?.name || ""}`;
  } else if (isSpaceContext.value) {
    return `${currentSpace?.value?.name || ""}`;
  } else if (isCampaignContext.value) {
    return `${currentCampaign?.value?.campaign?.internal_name || ""}`;
  }
});

function changeOrganisation(organisation: Organisation) {
  router.push({
    name: "space-selector",
    params: {
      companyId: organisation.id,
    }
  });
}

function changeSpace(space: Space) {
  // Changing space when we are already in a campaign context
  if (!!appStore.currentCampaignId) {
    router.push({
      name: "campaign-selector",
      params: {
        spaceId: space.workspace_id,
      }
    });
  } else {
    router.push({
      params: {
        spaceId: space.workspace_id,
      }
    });
  }
}

function changeCampaign(campaign: Campaign) {
  router.push({
    //name: "campaign",
    params: {
      campaignId: campaign.id,
    }
  });
}


</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.btn {
  max-width: 270px;
  flex-shrink: 1;
}

.label-text {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-weight: 500;
}

.context-menu {
  max-width: 350px;
  max-height: 250px;
  overflow-y: auto;

  .context-menu__item {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    cursor: pointer;

    padding: 4px 8px;
    border-radius: 4px;

    &:hover {
      background-color: var(--primary-300-color);
      color: $secondary-color;
    }
  }

  .text-bold {
    font-weight: 600;
  }
}
</style>